import { tv, type VariantProps } from "tailwind-variants";

import { link, solid, outline, ghost, solidOutline } from "./button.colors";

export const buttonVariant = tv(
  {
    slots: {
      base: [
        "inline-flex",
        "items-center",
        "box-border",
        "no-underline",
        "appearance-none",
        "outline-none",
        "select-none",
        "subpixel-antialiased",
        "overflow-hidden",
        "tap-highlight-transparent",
        "transition-colors duration-300",
        "disabled:pointer-events-none",
        "disabled:opacity-50",
        "focus-visible:z-10",
        "focus-visible:outline-2",
        "focus-visible:outline-focus",
        "[&>svg]:outline-none",
        "[&>svg]:shrink-0",
      ],
      image: ["max-w-md", "pointer-events-none"],
      loading: [],
    },
    variants: {
      appearance: {
        link: "",
        solid: "",
        outline: "",
        ghost: "",
        solidOutline: "",
      },
      color: {
        white: "",
        black: "",
        gray: "",
        grayLight: "",
        grayDark: "",
      },
      size: {
        xs: {
          base: ["text-display-12", "gap-x-2", "h-6", "[&>svg]:w-3", "[&>svg]:h-3"],
        },
        sm: {
          base: ["text-display-14", "gap-x-2", "h-8", "[&>svg]:w-4", "[&>svg]:h-4"],
        },
        md: {
          base: ["text-display-16", "gap-x-2", "h-10", "[&>svg]:w-5", "[&>svg]:h-5"],
        },
        lg: {
          base: ["text-5", "gap-x-2", "h-12", "[&>svg]:w-6", "[&>svg]:h-6"],
        },
      },
      weight: {
        regular: "font-regular",
        medium: "font-medium",
        demibold: "font-demibold",
        bold: "font-bold",
      },
      fluid: {
        none: {
          base: [],
        },
        all: {
          base: ["flex w-full"],
        },
        mobile: {
          base: ["flex w-full md:w-auto"],
        },
        desktop: {
          base: ["flex w-auto lg:w-full"],
        },
      },
      rounded: {
        true: "rounded-full",
      },
      isIconOnly: {
        true: "flex justify-center",
      },
      wrap: {
        false: "whitespace-nowrap",
        true: "whitespace-normal",
      },
    },
    compoundVariants: [
      // button
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        class: {
          base: "border justify-center text-center",
        },
      },
      {
        appearance: ["solid", "outline", "ghost"],
        class: {
          base: "border-transparent",
        },
      },

      // link
      {
        appearance: ["link"],
        class: {
          base: ["h-auto rounded-sm", "font-demibold"],
        },
      },

      // link / color
      {
        appearance: "link",
        color: "white",
        class: {
          base: link.white,
        },
      },
      {
        appearance: "link",
        color: "black",
        class: {
          base: link.black,
        },
      },
      {
        appearance: "link",
        color: "gray",
        class: {
          base: link.gray,
        },
      },
      {
        appearance: "link",
        color: "grayDark",
        class: {
          base: link.grayDark,
        },
      },

      // link / size
      {
        appearance: "link",
        size: "xs",
        class: {
          base: ["text-2"],
        },
      },
      {
        appearance: "link",
        size: "sm",
        class: {
          base: ["text-3"],
        },
      },
      {
        appearance: "link",
        size: "md",
        class: {
          base: ["text-4"],
        },
      },
      {
        appearance: "link",
        size: "lg",
        class: {
          base: ["text-6"],
        },
      },

      // solid / color
      {
        appearance: "solid",
        color: "white",
        class: {
          base: solid.white,
        },
      },
      {
        appearance: "solid",
        color: "black",
        class: {
          base: solid.black,
        },
      },
      {
        appearance: "solid",
        color: "gray",
        class: {
          base: solid.gray,
        },
      },
      {
        appearance: "solid",
        color: "grayDark",
        class: {
          base: solid.grayDark,
        },
      },
      {
        appearance: "solid",
        color: "grayLight",
        class: {
          base: solid.grayLight,
        },
      },

      // outline / color
      {
        appearance: "outline",
        color: "white",
        class: {
          base: outline.white,
        },
      },
      {
        appearance: "outline",
        color: "black",
        class: {
          base: outline.black,
        },
      },
      {
        appearance: "outline",
        color: "gray",
        class: {
          base: outline.gray,
        },
      },
      {
        appearance: "outline",
        color: "grayDark",
        class: {
          base: outline.grayDark,
        },
      },

      // ghost / color
      {
        appearance: "ghost",
        color: "white",
        class: {
          base: ghost.white,
        },
      },
      {
        appearance: "ghost",
        color: "black",
        class: {
          base: ghost.black,
        },
      },
      {
        appearance: "ghost",
        color: "gray",
        class: {
          base: ghost.gray,
        },
      },
      {
        appearance: "ghost",
        color: "grayDark",
        class: {
          base: ghost.grayDark,
        },
      },

      // solidOutline / color
      {
        appearance: "solidOutline",
        color: "black",
        class: {
          base: solidOutline.black,
        },
      },

      // Focus
      {
        appearance: ["solid", "outline", "ghost"],
        className: "lg:focus-visible:outline-offset-2",
      },
      {
        appearance: "link",
        className: "lg:focus-visible:outline-offset-8",
      },
      {
        color: ["white", "black", "gray", "grayDark"],
        className: {
          base: "focus-visible:outline-blue-400",
        },
      },

      // button / size
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: "xs",
        isIconOnly: false,
        class: {
          base: ["px-2 h-6"],
        },
      },
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: "sm",
        isIconOnly: false,
        class: {
          base: ["px-3"],
        },
      },
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: "md",
        isIconOnly: false,
        class: {
          base: ["px-4"],
        },
      },
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: "lg",
        isIconOnly: false,
        class: {
          base: ["px-6", "font-demibold"],
        },
      },

      // button / size / radius
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: "xs",
        isIconOnly: false,
        rounded: false,
        class: {
          base: ["rounded-sm"],
        },
      },
      {
        appearance: ["solid", "outline", "ghost", "solidOutline"],
        size: ["sm", "md", "lg"],
        isIconOnly: false,
        rounded: false,
        class: {
          base: ["rounded-2xl"],
        },
      },

      // isIconOnly
      {
        isIconOnly: true,
        size: "xs",
        class: {
          base: ["w-6 h-6"],
        },
      },
      {
        isIconOnly: true,
        size: "sm",
        class: {
          base: ["w-8 h-8"],
        },
      },
      {
        isIconOnly: true,
        size: "md",
        class: {
          base: ["w-10", "[&>svg]:w-4.5", "[&>svg]:h-4.5"],
        },
      },
      {
        isIconOnly: true,
        size: "lg",
        class: {
          base: ["w-12", "[&>svg]:w-5", "[&>svg]:h-5"],
        },
      },
      {
        appearance: "link",
        isIconOnly: true,
        class: {
          base: ["w-auto h-auto"],
        },
      },
    ],
    defaultVariants: {
      appearance: "link",
      size: "md",
      color: "grayDark",
      weight: "medium",
      isIconOnly: false,
      wrap: false,
    },
  },
  { twMerge: false },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariant>;

export type ButtonReturnVariantProps = ReturnType<typeof buttonVariant>;
